import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAmenities, setAmenitiesLoading, setAmenitiesError } from "../redux/actions/dashboard";
import amenitiesService from "../services/amenitiesService";
import logger from "../utils/logger";


/**
 * Custom hook to manage amenities data with Redux caching
 * - Loads amenities only once per session
 * - Caches data in Redux store
 * - Automatically clears on logout
 */
export const useAmenities = () => {
  const dispatch = useDispatch();
  const { amenities, amenitiesLoading, amenitiesError } = useSelector(state => ({
    amenities: state.dashboard?.amenities || [],
    amenitiesLoading: state.dashboard?.amenitiesLoading || false,
    amenitiesError: state.dashboard?.amenitiesError || null,
  }));

  const loadAmenities = useCallback(async () => {
    // If amenities are already loaded, don't fetch again
    if (amenities.length > 0) {
      return amenities;
    }

    // If already loading, don't start another request
    if (amenitiesLoading) {
      return amenities;
    }

    try {
      dispatch(setAmenitiesLoading(true));
      dispatch(setAmenitiesError(null));

      const response = await amenitiesService.getAllAmenities();

      if (response && response.data && response.data.content) {
        dispatch(setAmenities(response.data.content));
        return response.data.content;
      } else {
        throw new Error("No amenities data received");
      }
    } catch (error) {
      logger.error("Error loading amenities:", error);
      dispatch(setAmenitiesError(error.message || "Failed to load amenities"));
      return [];
    }
  }, [amenities, amenitiesLoading, dispatch]);

  // Auto-load amenities when hook is first used
  useEffect(() => {
    // If there's an error, clear it and try again
    if (amenitiesError && amenitiesError.includes("billingService")) {
      dispatch(setAmenitiesError(null));
      loadAmenities();
    } else if (amenities.length === 0 && !amenitiesLoading && !amenitiesError) {
      loadAmenities();
    }
  }, [amenities.length, amenitiesLoading, amenitiesError, loadAmenities, dispatch]);

  const clearAmenitiesError = () => {
    dispatch(setAmenitiesError(null));
    loadAmenities();
  };

  return {
    amenities,
    amenitiesLoading,
    amenitiesError,
    loadAmenities,
    clearAmenitiesError,
    hasAmenities: amenities.length > 0,
  };
};
